import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, API } from '../api';
import { fmtWh } from '../time';
import { useScanWedge } from '../scanWedge';
import { scanSound } from '../scanSound';
import { useDeskSignIn, isBadgeRequired } from '../returns/DeskSignIn';
import { KIND_TEXT } from './TasksPage';

// One task (docs/TASKS.md, 1.11). On a P&P station the person signs in with badge / PIN (the `tasks` right); every
// scan, tap, skip and the finish is recorded under them. The scanner is read by useScanWedge (any keyboard layout).
// A move task (Fernando 3.11): the Å shelf is chosen ONCE at the top and every scanned piece goes there; every scan
// answers in a big card over the list; a piece the stock does not know asks "did you find this piece?" — Yes adds it.

interface Item {
  id: number; seq: number; label: string; name: string | null; size: string | null; article_number: string | null; barcode: string | null;
  qty: number; done_qty: number; from_place: string | null; to_place: string | null; from_display: string | null; to_display: string | null;
  status: 'todo' | 'done' | 'skipped'; done_by: string | null; done_at: string | null; done_how: string | null; note: string | null;
  result: any;
}
interface Place { code: string; display: string; pieces: number }
interface Write { delta: number; status: string; reason: string | null }
interface Task {
  id: number; title: string; instruction: string | null; kind: string; status: string; state: any;
  created_by: string; created_at: string; started_by: string | null; started_at: string | null;
  done_by: string | null; done_at: string | null; done_note: string | null; cancelled_by: string | null; cancel_note: string | null;
  items: Item[]; total: number; done: number; skipped: number; kept?: number; stopped?: number; pieces_total?: number; pieces_done?: number;
  events?: { id: number; at: string; actor: string; device: string | null; event: string; detail: any; item_id: number | null }[];
  places?: Place[];
  /** live per open item (1.11): an order that keeps the piece, and what the stock says now */
  checks?: Record<string, Check>;
  /** a move task's found pieces: their stock writes (3.11) */
  writes?: Record<string, Write[]>;
}
interface KeptBy { order_number: string; tab: string; tab_label: string; waits_this: boolean; waits_for: string[] }
interface Check { orders: KeptBy[]; stock: number | null; stock_source: string | null }
/** the answer to the last scan, big over the list (3.11: "there are no popups") */
interface Card {
  tone: 'ok' | 'warn' | 'bad' | 'ask' | 'choose';
  title: string; text?: string;
  itemId?: number | null;   // warn: the item to leave on Ä
  token?: string;           // ask: the server's one-time question
  code?: string;            // choose: the scan waiting for a shelf
}

const keptText = (o: KeptBy) => `kept for ${o.order_number} (${o.tab_label}${o.waits_this ? ' — the order waits for this piece'
  : o.waits_for.length ? ` — the order waits for ${o.waits_for.slice(0, 3).join(', ')}` : ''})`;

const EVENT_TEXT: Record<string, string> = {
  created: 'made the task', started: 'started', item_done: 'done', item_skipped: 'not found', item_undone: 'undone',
  done: 'finished the task', stopped: 'stopped the task here', reopened: 'reopened', cancelled: 'cancelled', note: '', items_added: 'added items',
  item_added: 'added', place_chosen: 'chose the shelf', found_added: 'found a piece the stock did not know — Yes, added',
  found_declined: 'answered No',
};

const writeText = (ws: Write[]) => ws.map(w => `stock ${w.delta > 0 ? '+' : ''}${w.delta} ${
  w.status === 'ok' ? '✓' : w.status === 'pending' ? '(sending…)' : w.status === 'cancelled' ? '(not written — writes are off)' : '(in Review)'}`).join(' · ');

const CARD_TONE: Record<Card['tone'], string> = {
  ok: 'border-emerald-500 bg-emerald-950', warn: 'border-amber-400 bg-amber-950', bad: 'border-red-500 bg-red-950',
  ask: 'border-sky-400 bg-slate-900', choose: 'border-sky-400 bg-slate-900',
};

export default function TaskScreen() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sign = useDeskSignIn('tasks', `${API}/tasks/who`, { eager: false });
  const [t, setT] = useState<Task | null>(null);
  const [err, setErr] = useState('');
  const [card, setCard] = useState<Card | null>(null);
  const [note, setNote] = useState('');
  const [skipFor, setSkipFor] = useState<Item | null>(null);
  const [skipNote, setSkipNote] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [stopAsk, setStopAsk] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/tasks/${id}`);
    if (!r.ok) { setErr('Could not load the task'); return; }
    setT(await r.json()); setErr('');
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // a green card closes by itself; the others wait for the next scan, a tap or ✕
  useEffect(() => {
    if (card?.tone !== 'ok') return;
    const h = window.setTimeout(() => setCard(c => (c === card ? null : c)), 2000);
    return () => window.clearTimeout(h);
  }, [card]);

  /** a write as the signed-in person; a 401 badge_required opens the sign-in and retries. null = the sign-in is asked */
  const call = useCallback(async (path: string, body?: any, retry?: () => void): Promise<{ ok: boolean; d: any } | null> => {
    const r = await authFetch(`${API}/tasks/${id}${path}`, { method: 'POST', body: JSON.stringify(body || {}) });
    if (await isBadgeRequired(r)) { sign.askAgain(retry); return null; }
    const d = await r.json().catch(() => ({}));
    if (r.ok && d.task) setT(cur => cur ? { ...cur, ...d.task, events: cur.events, checks: cur.checks, writes: cur.writes, places: d.task.places || cur.places } : d.task);
    else if (r.ok) await load();
    return { ok: r.ok, d };
  }, [id, sign, load]);

  /** the refusal as a card: kept → amber with Leave on Ä; no shelf → the shelf choice; else red */
  const refusal = useCallback((d: any, code?: string) => {
    const det = d?.detail;
    const text = typeof det === 'string' ? det : det?.message || 'Something went wrong — try again';
    scanSound('error');
    if (det?.code === 'choose_place') setCard({ tone: 'choose', title: 'Choose the Å shelf first', text: 'Tap the shelf the pieces go on — or scan its label', code });
    else if (det?.code === 'reserved') setCard({ tone: 'warn', title: 'Leave it on Ä', text, itemId: det.item_id });
    else setCard({ tone: 'bad', title: text });
  }, []);

  /** what a move / scan answer means, as a card */
  const answered = useCallback((d: any) => {
    const of = (x: any) => x.task && x.task.pieces_total ? ` · ${x.task.pieces_done} of ${x.task.pieces_total}` : '';
    if (d.did === 'placed') { scanSound(d.stock_note ? 'error' : 'ok'); setCard({ tone: d.stock_note ? 'warn' : 'ok', title: `✓ ${d.label} → ${d.place_display || d.place}`, text: `${d.stock_note || 'moved'}${of(d)}` }); }
    else if (d.did === 'scanned') { scanSound(d.stock_note ? 'error' : 'ok'); setCard({ tone: d.stock_note ? 'warn' : 'ok', title: `✓ ${d.label} ${d.done_qty}/${d.qty}`, text: d.stock_note || undefined }); }
    else if (d.did === 'ask_found') { scanSound('error'); setCard({ tone: 'ask', title: d.label, text: d.message, token: d.token }); }
    else if (d.did === 'place_set') { scanSound('ok'); setCard({ tone: 'ok', title: `Now putting on ${d.place_display}`, text: 'every piece you scan goes there' }); }
    else if (d.did === 'declined') setCard(null);
    else if (d.did === 'found_added') {
      const w = d.write?.status;
      if (w === 'ok') { scanSound('ok'); setCard({ tone: 'ok', title: `✓ Added to the stock: ${d.label} → ${d.place_display}`, text: `the stock says ${d.stock_now ?? (Number(d.stock_said) + 1)} now${of(d)}` }); }
      else {
        scanSound('error');
        setCard({ tone: 'bad', title: `${d.label} is on ${d.place_display} — but the stock was NOT raised`,
          text: w === 'cancelled' ? 'Stock writes are switched off — it is listed in Review when the task is finished'
            : w === 'pending' ? 'No answer from the shop yet — the WMS checks it; see the row' : `The shop refused it (${d.write?.reason || 'see Review'}) — it is in Review` });
      }
      load();
    }
  }, [load]);

  const scan = useCallback((code: string) => {
    const go = async () => {
      setCard(null);
      const res = await call('/scan', { code }, go);
      if (!res) return;
      if (!res.ok) { refusal(res.d, code); return; }
      answered(res.d);
    };
    go();
  }, [call, refusal, answered]);

  const choosePlace = useCallback((place: string, pending?: string) => {
    const go = async () => {
      const res = await call('/place', { place }, go);
      if (!res) return;
      if (!res.ok) { refusal(res.d); return; }
      if (pending) scan(pending); else answered(res.d);
    };
    go();
  }, [call, refusal, answered, scan]);

  const answerFound = useCallback((yes: boolean) => {
    if (!card?.token) return;
    const token = card.token;
    const go = async () => {
      setBusy(true);
      try {
        const res = await call('/found', { token, yes }, go);
        if (!res) return;
        if (!res.ok) { refusal(res.d); return; }
        answered(res.d);
      } finally { setBusy(false); }
    };
    go();
  }, [card, call, refusal, answered]);

  // the scanner listens only once the task was STARTED (Fernando 1.11: description first, then "Start task", which asks
  // for the badge or PIN) — and waits while a question is open (a scan never answers it)
  const active = !!t && t.status === 'in_progress' && t.kind !== 'check' && t.kind !== 'tick' && !skipFor && card?.tone !== 'ask';
  useScanWedge(code => scan(code), { enabled: active, skipFields: true });
  const startTask = useCallback(() => { call('/start', {}, startTask); }, [call]);

  if (err) return <div className="p-6 text-red-400">{err}</div>;
  if (!t) return <div className="p-6 text-gray-500">Loading…</div>;
  const kind = KIND_TEXT[t.kind] || { label: t.kind, hint: '' };
  const live = t.status === 'in_progress';           // started: items, scanner, finish
  const notStarted = t.status === 'open';
  const isMove = t.kind === 'move';
  const shelf = isMove ? (t.places || []).find(p => p.code === t.state?.place) : undefined;
  const next = t.items.find(i => i.status === 'todo');
  const openItems = t.items.filter(i => i.status === 'todo');
  const closedItems = t.items.filter(i => i.status !== 'todo');
  const tapDone = (i: Item) => {
    const go = async () => {
      const res = await call(`/items/${i.id}/done`, {}, go);
      if (!res) return;
      if (!res.ok) { refusal(res.d); return; }
      if (isMove) answered(res.d);
    };
    go();
  };
  const skip = async () => {
    if (!skipFor || !skipNote.trim()) return;
    const res = await call(`/items/${skipFor.id}/skip`, { note: skipNote }, skip);
    if (res && !res.ok) refusal(res.d);
    if (res?.ok) { setSkipFor(null); setSkipNote(''); }
  };
  const finishTask = async () => {
    setBusy(true);
    try {
      const res = await call('/done', { note }, finishTask);
      if (res && !res.ok) refusal(res.d);
      if (res?.ok) { setNote(''); await load(); }
    } finally { setBusy(false); }
  };
  // a manager stops the task here (4.11): the open items close as not done — not "Not found", no Review case
  const stopTask = async () => {
    setBusy(true);
    try {
      const res = await call('/done', { note, stop: true }, stopTask);
      if (res && !res.ok) refusal(res.d);
      if (res?.ok) { setNote(''); setStopAsk(false); await load(); }
    } finally { setBusy(false); }
  };
  const leaveOnShelf = (itemId: number) => {
    const go = async () => {
      const res = await call(`/items/${itemId}/skip`, { reason: 'reserved' }, go);
      if (res && !res.ok) refusal(res.d);
      if (res?.ok) setCard(null);
    };
    go();
  };
  const undo = (i: Item) => {
    const go = async () => {
      const res = await call(`/items/${i.id}/undo`, {}, go);
      if (res && !res.ok) refusal(res.d);
      if (res?.ok) {
        if (res.d.write) setCard({ tone: res.d.write.status === 'ok' ? 'ok' : 'bad', title: `${i.label}: undone`,
          text: res.d.write.status === 'ok' ? `the stock was lowered again (${res.d.write.delta})` : 'the stock could NOT be lowered again — see the row' });
        await load();
      }
    };
    go();
  };

  const itemRow = (i: Item) => {
    const isNext = next?.id === i.id && live;
    const c = i.status === 'todo' ? t.checks?.[String(i.id)] : undefined;
    const kept = c?.orders?.[0];
    const sd = i.result?.stock_difference;
    const ws = t.writes?.[String(i.id)];
    const undone = i.done_how === 'undone';
    const stopped = i.done_how === 'stopped';
    return (
      <div key={i.id} className={`rounded-xl border px-4 py-3 flex flex-wrap items-center gap-3 ${
        undone || stopped ? 'border-gray-800 bg-[#14161f] opacity-60'
        : i.status === 'done' ? 'border-emerald-900 bg-emerald-950/30' : i.status === 'skipped' ? 'border-red-900 bg-red-950/30'
        : isNext ? 'border-blue-500 bg-blue-950/30' : 'border-gray-800 bg-[#1a1d27]'}`}>
        <span className="text-gray-500 text-xs w-6">{i.seq}</span>
        <div className="flex-1 min-w-[200px]">
          <div className={`font-semibold ${i.status === 'todo' ? 'text-white' : 'text-gray-300'}`}>{i.label}{i.qty > 1 ? <span className="text-gray-400"> × {i.qty}</span> : null}
            {i.result?.added_by_scan && <span className="ml-2 text-xs font-normal text-sky-300">added by a scan</span>}</div>
          <div className="text-xs text-gray-500">{i.name}{i.from_display ? ` · at ${i.from_display}` : ''}{!isMove && i.to_display ? ` → ${i.to_display}` : ''}
            {i.status !== 'todo' && <span className={stopped ? 'text-gray-400' : i.status === 'skipped' && i.done_how !== 'reserved' && !undone ? 'text-red-300' : 'text-emerald-300'}> · {undone ? 'undone' : stopped ? 'stopped' : i.done_how === 'reserved' ? 'left on the shelf' : i.status === 'skipped' ? 'not found' : 'done'} by {i.done_by} {i.done_at ? fmtWh(i.done_at, 'time') : ''}{i.done_how && i.status === 'done' ? ` (${i.done_how})` : ''}{i.result?.place ? ` → ${i.result.place}` : ''}{i.note && !undone ? ` — "${i.note}"` : ''}</span>}
          </div>
          {/* what the WMS knows right now (1.11): an order keeps the piece; the stock figure */}
          {kept && <div className="mt-1 text-sm font-semibold text-amber-300">⚑ {keptText(kept)} — do not move it, leave it on {i.from_display || 'the shelf'}</div>}
          {c && c.stock !== null && c.stock !== undefined && (c.stock === 0 || c.stock !== i.qty) && (
            <div className={`mt-1 text-xs ${c.stock === 0 ? 'text-red-300' : 'text-gray-400'}`}>
              {c.stock === 0 ? `Stock says 0 now${c.stock_source === 'live' ? ' (WGR, live)' : ''}${isMove ? ' — if the piece is here, scan it: the WMS asks to add it to the stock' : ' — if the piece is here, scan it anyway; it is reported as a stock difference'}`
                : `stock says ${c.stock} now (the list says ${i.qty})`}
            </div>
          )}
          {i.result?.found ? <div className="mt-1 text-xs text-sky-300">found — the stock did not know it{ws ? ` · ${writeText(ws)}` : ''}</div> : ws ? <div className="mt-1 text-xs text-gray-400">{writeText(ws)}</div> : null}
          {sd && <div className="mt-1 text-xs text-red-300">stock difference: the stock said {sd.says}, found {sd.found}{sd.write ? ' — the stock was not raised' : ''} — reported</div>}
        </div>
        {i.status === 'todo' && live && kept && (
          <button onClick={() => leaveOnShelf(i.id)} className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-black text-sm font-bold">Leave on {i.from_display || 'the shelf'}</button>
        )}
        {i.status === 'todo' && live && !kept && (
          <span className="flex gap-2">
            <button onClick={() => tapDone(i)} className="px-3 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold">{t.kind === 'check' ? 'Checked ✓' : isMove ? `Moved ✓${shelf ? ` → ${shelf.display}` : ''}` : 'Done ✓'}</button>
            <button onClick={() => { setSkipFor(i); setSkipNote(''); }} className="px-3 py-2 rounded-lg bg-gray-700 hover:bg-red-800 text-white text-sm">{t.kind === 'check' ? 'Cannot' : 'Not found'}</button>
          </span>
        )}
        {i.status !== 'todo' && !undone && live && <button onClick={() => undo(i)} className="text-xs text-gray-500 hover:text-white">undo</button>}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 text-gray-200 max-w-4xl mx-auto">
      {sign.box}
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <button onClick={() => navigate('/tasks')} className="text-sm text-gray-400 hover:text-white">← Tasks</button>
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-300">{kind.label}</span>
        <span className="text-xs text-gray-500">#{t.id} · made by {t.created_by} {fmtWh(t.created_at, 'datetime')}</span>
        {sign.who?.station && <span className="ml-auto text-xs text-gray-400">{sign.who.person ? `working: ${sign.who.name || sign.who.person}` : 'nobody signed in — the first scan asks for your badge'}</span>}
      </div>
      <h1 className="text-2xl font-bold text-white">{t.title}</h1>
      {t.instruction && <p className="mt-2 text-lg text-gray-100 whitespace-pre-wrap">{t.instruction}</p>}

      {t.status === 'done' && <div className="mt-3 rounded-lg border border-emerald-700 bg-emerald-900/20 px-4 py-2 text-emerald-200">Done by {t.done_by} {t.done_at ? fmtWh(t.done_at, 'datetime') : ''}{t.done_note ? ` — "${t.done_note}"` : ''}{t.skipped ? ` · ${t.skipped} not found` : ''}{t.kept ? ` · ${t.kept} left for an order` : ''}{t.stopped ? ` · stopped with ${t.stopped} not done` : ''}</div>}
      {t.status === 'cancelled' && <div className="mt-3 rounded-lg border border-red-800 bg-red-900/20 px-4 py-2 text-red-200">Cancelled by {t.cancelled_by}{t.cancel_note ? ` — "${t.cancel_note}"` : ''}</div>}

      {notStarted && (
        // what we want to achieve, then Start — on a station the start asks for the badge or PIN (post → 401 → the box)
        <div className="mt-6 rounded-2xl border-2 border-blue-700 bg-blue-950/30 p-6 text-center">
          <div className="text-gray-300 text-lg">{kind.hint}{t.total ? ` · ${t.total} item${t.total === 1 ? '' : 's'}` : ''}</div>
          {(() => {
            const cs = Object.values(t.checks || {});
            const keptN = cs.filter(c => c.orders.length).length, zeroN = cs.filter(c => c.stock === 0).length;
            return (keptN || zeroN) ? (
              <div className="mt-2 text-sm text-amber-300">
                {keptN ? `${keptN} item${keptN === 1 ? ' is' : 's are'} kept for an order now — leave ${keptN === 1 ? 'it' : 'them'} on the shelf` : ''}
                {keptN && zeroN ? ' · ' : ''}{zeroN ? `${zeroN} item${zeroN === 1 ? '' : 's'}: the stock says 0 now` : ''}
              </div>
            ) : null;
          })()}
          <button onClick={startTask} className="mt-4 px-12 py-5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-3xl font-black">Start task</button>
          <div className="mt-3 text-sm text-gray-400">{sign.who?.station && !sign.who.person ? 'You will be asked to scan your badge or enter your PIN.' : 'Your steps are recorded under your name.'}</div>
        </div>
      )}

      {t.kind !== 'tick' && !notStarted && (
        <div className="mt-4">
          {isMove && live && (
            // Fernando 3.11: "Let me rather choose one and now everything I scan goes into that"
            <div className="sticky top-0 z-10 mb-3 rounded-xl border border-gray-700 bg-[#0f1117]/95 px-3 py-2 flex flex-wrap items-center gap-2">
              <span className="text-gray-300 font-semibold mr-1">Putting on:</span>
              {(t.places || []).map(p => (
                <button key={p.code} onClick={() => choosePlace(p.code)}
                  className={`px-5 py-3 rounded-xl text-xl font-black ${p.code === t.state?.place ? 'bg-amber-500 text-black ring-4 ring-amber-300' : 'bg-gray-800 text-gray-200 hover:bg-gray-700'}`}>
                  {p.display} <span className="text-sm font-semibold opacity-70">· {p.pieces}</span>
                </button>
              ))}
              {!shelf && <span className="text-amber-300 text-sm">← choose the shelf first (or scan its label)</span>}
            </div>
          )}
          <div className="flex items-center gap-3 mb-2">
            <div className="text-sm text-gray-400">
              {isMove && t.pieces_total ? `${t.pieces_done} of ${t.pieces_total} pieces` : `${t.done} of ${t.total} done`}{t.skipped ? ` · ${t.skipped} not found` : ''}
            </div>
            <div className="flex-1 h-2 rounded bg-gray-800"><div className="h-2 rounded bg-emerald-600" style={{ width: `${t.total ? Math.round(((t.total - openItems.length) / t.total) * 100) : 0}%` }} /></div>
          </div>
          {live && t.kind !== 'check' && (
            <form onSubmit={e => { e.preventDefault(); if (typed.trim()) { scan(typed.trim()); setTyped(''); } }} className="mb-3">
              <input ref={inputRef} value={typed} onChange={e => setTyped(e.target.value)} placeholder={isMove && shelf ? `Scan a piece — it goes on ${shelf.display} (or type its code)` : 'Scan a piece… (or type its code)'}
                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white text-center text-lg" autoComplete="off" />
            </form>
          )}
          {/* open items first, in walking order; done ones folded at the bottom (3.11: no hunting for the row) */}
          <div className="space-y-2">{openItems.map(itemRow)}</div>
          {openItems.length === 0 && <div className="text-emerald-300 text-sm">Every item is done or marked.</div>}
          {closedItems.length > 0 && (
            <div className="mt-4">
              <button onClick={() => setShowDone(s => !s)} className="text-sm text-gray-400 hover:text-white">{showDone ? '▾' : '▸'} Done ({closedItems.length})</button>
              {showDone && <div className="mt-2 space-y-2">{closedItems.map(itemRow)}</div>}
            </div>
          )}
        </div>
      )}

      {card && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-black/50"
          onClick={() => { if (card.tone !== 'ask' && card.tone !== 'choose') setCard(null); }}>
          <div className={`w-full max-w-2xl rounded-3xl border-4 p-6 sm:p-8 text-center shadow-2xl ${CARD_TONE[card.tone]}`} onClick={e => e.stopPropagation()}>
            <div className="text-3xl sm:text-4xl font-black text-white break-words">{card.title}</div>
            {card.text && <div className="mt-3 text-xl text-gray-100">{card.text}</div>}
            {card.tone === 'ask' && (
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button disabled={busy} onClick={() => answerFound(true)} className="px-6 py-5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-2xl font-black">
                  Yes — add 1 to the stock{shelf ? ` and put it on ${shelf.display}` : ''}
                </button>
                <button disabled={busy} onClick={() => answerFound(false)} className="px-6 py-5 rounded-2xl bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white text-2xl font-bold">No</button>
              </div>
            )}
            {card.tone === 'choose' && (
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                {(t.places || []).map(p => (
                  <button key={p.code} onClick={() => { const c = card.code; setCard(null); choosePlace(p.code, c); }}
                    className="px-8 py-5 rounded-2xl bg-amber-500 hover:bg-amber-400 text-black text-3xl font-black">{p.display}</button>
                ))}
              </div>
            )}
            {card.tone === 'warn' && card.itemId && (
              <div className="mt-6"><button onClick={() => leaveOnShelf(card.itemId!)} className="px-6 py-4 rounded-2xl bg-amber-500 hover:bg-amber-400 text-black text-2xl font-black">Leave on Ä</button></div>
            )}
            {card.tone !== 'ask' && (
              <button onClick={() => setCard(null)} className="mt-6 text-gray-300 hover:text-white text-lg">{card.tone === 'ok' ? 'OK' : '✕ Close'}</button>
            )}
          </div>
        </div>
      )}

      {skipFor && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-6" onClick={() => setSkipFor(null)}>
          <div className="bg-[#1a1d27] border border-gray-700 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="text-white font-bold text-lg">{skipFor.label}</div>
            <div className="text-gray-400 text-sm mb-3">{t.kind === 'check' ? 'Why can it not be done?' : 'Why was it not found?'} A word is enough — a manager reads it.</div>
            <input autoFocus value={skipNote} onChange={e => setSkipNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') skip(); }}
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white" placeholder="not on the shelf / damaged / wrong size" />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setSkipFor(null)} className="px-3 py-2 rounded bg-gray-700 text-white">Cancel</button>
              <button onClick={skip} disabled={!skipNote.trim()} className="px-4 py-2 rounded bg-red-700 hover:bg-red-600 disabled:opacity-40 text-white font-semibold">Mark</button>
            </div>
          </div>
        </div>
      )}

      {live && (
        <div className="mt-6 rounded-xl border border-gray-800 bg-[#1a1d27] p-4 flex flex-wrap items-center gap-3">
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="a note for the record (optional)" className="flex-1 min-w-[220px] bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
          <button onClick={finishTask} disabled={busy || (t.kind !== 'tick' && !!next)} className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-lg font-black"
            title={t.kind !== 'tick' && next ? 'Every item first: done, or Not found with a note' : ''}>{t.kind === 'tick' ? 'Done ✓' : 'Finish the task ✓'}</button>
          {t.kind !== 'tick' && next && (stopAsk ? (
            <span className="flex flex-wrap items-center gap-2 text-sm text-amber-200">
              Stop here? The {openItems.length} open item{openItems.length === 1 ? '' : 's'} close as not done; what is done stays done.
              <button onClick={stopTask} disabled={busy} className="px-3 py-2 rounded bg-amber-700 hover:bg-amber-600 disabled:opacity-40 text-white font-semibold">Stop the task</button>
              <button onClick={() => setStopAsk(false)} className="px-3 py-2 rounded bg-gray-700 text-white">Cancel</button>
            </span>
          ) : (
            <button onClick={() => setStopAsk(true)} disabled={busy} className="px-4 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white font-semibold"
              title="Managers: close the open items as not done and end the task">Stop here</button>
          ))}
        </div>
      )}

      <div className="mt-6">
        <button onClick={() => setShowLog(s => !s)} className="text-sm text-gray-400 hover:text-white">{showLog ? 'Hide' : 'Show'} the record ({t.events?.length || 0} steps)</button>
        {showLog && (
          <div className="mt-2 text-xs text-gray-400 space-y-1">
            {(t.events || []).map(e => (
              <div key={e.id}><span className="text-gray-500">{fmtWh(e.at, 'datetime')}</span> · <span className="text-gray-200">{e.actor}</span>{e.device ? <span className="text-gray-600"> @ {e.device}</span> : null} · {EVENT_TEXT[e.event] ?? e.event}
                {e.detail?.label ? ` ${e.detail.label}` : ''}{e.detail?.in_hand ? ` ${e.detail.in_hand} in hand` : ''}{e.detail?.how ? ` (${e.detail.how})` : ''}{e.detail?.place ? ` → ${e.detail.place}` : ''}
                {e.detail?.items ? ` ${e.detail.items} item${e.detail.items === 1 ? '' : 's'}${e.detail.pieces ? `, ${e.detail.pieces} pcs` : ''}` : ''}{e.detail?.note ? ` — "${e.detail.note}"` : ''}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
